/*
  Auto Needs — shared storefront script
  Loaded from layout/theme.liquid (deferred), right after Dawn's pubsub.js.

  Defines:
    <sticky-buy-bar>          snippets/sticky-buy-bar.liquid
    <advance-payment-badge>   snippets/advance-payment-badge.liquid
*/
(function () {
  'use strict';

  function onVariantChange(callback) {
    // Dawn declares these with `function` / `const` at the top level of constants.js and pubsub.js,
    // so they are shared globals but not window properties — reference them by bare name.
    if (
      typeof subscribe === 'function' &&
      typeof PUB_SUB_EVENTS !== 'undefined' &&
      PUB_SUB_EVENTS.variantChange
    ) {
      return subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        var variant = event && event.data ? event.data.variant : null;
        callback(variant);
      });
    }
    return null;
  }

  function formatPKR(paisa) {
    var rupees = Math.round(Number(paisa) / 100);
    return 'Rs. ' + rupees.toLocaleString('en-US') + ' PKR';
  }

  /* ---------------- Sticky buy bar ---------------- */
  if (!customElements.get('sticky-buy-bar')) {
    class StickyBuyBar extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('[data-sticky-form]');
        this.idInput = this.querySelector('[data-sticky-id]');
        this.priceEl = this.querySelector('[data-sticky-price]');
        this.compareEl = this.querySelector('[data-sticky-compare]');
        this.buyBtn = this.querySelector('[data-sticky-buy]');
        this.buyLabel = this.querySelector('[data-sticky-buy-label]');
        this.addBtn = this.querySelector('[data-sticky-add]');

        this.observeMainButton();

        if (this.form) {
          this.form.addEventListener('submit', () => {
            this.syncVariantId();
            if (this.buyBtn) this.buyBtn.classList.add('is-loading');
          });
        }

        if (this.addBtn) {
          this.addBtn.addEventListener('click', () => {
            const mainBtn = this.getMainButton();
            if (mainBtn && !mainBtn.disabled) {
              mainBtn.click();
              return;
            }
            if (this.form) {
              this.syncVariantId();
              const returnTo = this.form.querySelector('[name="return_to"]');
              if (returnTo) returnTo.value = '/cart';
              this.form.submit();
            }
          });
        }

        this.unsubscribe = onVariantChange((variant) => this.updateFromVariant(variant));

        // Restore the button if the shopper comes back with the browser Back button.
        this.onPageShow = () => {
          if (this.buyBtn) this.buyBtn.classList.remove('is-loading');
        };
        window.addEventListener('pageshow', this.onPageShow);
      }

      disconnectedCallback() {
        if (this.unsubscribe) this.unsubscribe();
        if (this.io) this.io.disconnect();
        if (this.onPageShow) window.removeEventListener('pageshow', this.onPageShow);
        document.body.classList.remove('an-sticky-visible');
      }

      // Looked up on demand, because Dawn can re-render the product form on variant change.
      getMainForm() {
        const forms = document.querySelectorAll('form[action*="/cart/add"]');
        for (const f of forms) {
          if (this.contains(f)) continue;
          if (f.closest('.quick-add-modal, .card-wrapper, .product-card-wrapper, quick-add-bulk')) continue;
          return f;
        }
        return null;
      }

      getMainButton() {
        const form = this.getMainForm();
        return form ? form.querySelector('[name="add"], button[type="submit"]') : null;
      }

      observeMainButton() {
        const target = this.getMainButton() || this.getMainForm();
        if (!target || !('IntersectionObserver' in window)) {
          this.setVisible(true);
          return;
        }
        this.io = new IntersectionObserver((entries) => {
          const entry = entries[0];
          // Show only after the main button has scrolled above the screen.
          this.setVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0);
        });
        this.io.observe(target);
      }

      setVisible(visible) {
        this.classList.toggle('is-visible', visible);
        this.setAttribute('aria-hidden', visible ? 'false' : 'true');
        if (visible) this.removeAttribute('inert');
        else this.setAttribute('inert', '');
        document.body.classList.toggle('an-sticky-visible', visible);
      }

      syncVariantId() {
        const form = this.getMainForm();
        if (!form || !this.idInput) return;
        const mainId = form.querySelector('[name="id"]');
        if (mainId && mainId.value) this.idInput.value = mainId.value;
      }

      updateFromVariant(variant) {
        if (!variant) {
          this.toggleButtons(false, 'Unavailable');
          return;
        }
        if (this.idInput) this.idInput.value = variant.id;
        if (this.priceEl) this.priceEl.textContent = formatPKR(variant.price);
        if (this.compareEl) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            this.compareEl.textContent = formatPKR(variant.compare_at_price);
            this.compareEl.hidden = false;
          } else {
            this.compareEl.hidden = true;
          }
        }
        this.toggleButtons(variant.available, variant.available ? 'Buy Now – COD' : 'Sold Out');
      }

      toggleButtons(enabled, label) {
        [this.buyBtn, this.addBtn].forEach((btn) => {
          if (!btn) return;
          if (enabled) btn.removeAttribute('disabled');
          else btn.setAttribute('disabled', '');
        });
        if (this.buyLabel && label) this.buyLabel.textContent = label;
      }
    }
    customElements.define('sticky-buy-bar', StickyBuyBar);
  }

  /* ---------------- Advance payment badge ---------------- */
  if (!customElements.get('advance-payment-badge')) {
    class AdvancePaymentBadge extends HTMLElement {
      connectedCallback() {
        this.threshold = parseInt(this.dataset.threshold, 10) || 500000;
        this.unsubscribe = onVariantChange((variant) => {
          if (variant && typeof variant.price !== 'undefined') {
            this.hidden = Number(variant.price) < this.threshold;
          }
        });
      }

      disconnectedCallback() {
        if (this.unsubscribe) this.unsubscribe();
      }
    }
    customElements.define('advance-payment-badge', AdvancePaymentBadge);
  }
})();
